// alarms.effects.ts
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, share, switchMap } from 'rxjs/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { AlarmManagementAction } from '@states/alarm-mangment/alarm-management.action-types';
import { AlarmManagementService } from '../../../services/alarm-management.service';
import { AppState } from '../../app.state';

@Injectable()
export class AlarmManagementEffects {

  public startGetActiveAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.startGetActiveAlarms),
      exhaustMap(() => [
        AlarmManagementAction.setAlarmsLoading({ isLoading: true }),
        AlarmManagementAction.getActiveAlarms(),
      ]),
    ),
  );

  public getActiveAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.getActiveAlarms),
      switchMap(() => {
        return this.alarmsService.getActiveAlarms()
          .pipe(
            switchMap(activeAlarms => [
              AlarmManagementAction.getActiveAlarmsSuccess({ activeAlarms }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
            catchError(error => [
              AlarmManagementAction.getActiveAlarmsFail(),
              SharedActions.showMessage({ error: 'Failed to load active alarms' }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  public startGetCanceledAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.startGetCanceledAlarms),
      exhaustMap(() => [
        AlarmManagementAction.setAlarmsLoading({ isLoading: true }),
        AlarmManagementAction.getCanceledAlarms(),
      ]),
    ),
  );

  public getCanceledAlarms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.getCanceledAlarms),
      switchMap(() => {
        return this.alarmsService.getCanceledAlarms()
          .pipe(
            switchMap(canceledAlarms => [
              AlarmManagementAction.getCanceledAlarmsSuccess({ canceledAlarms }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
            catchError(error => [
              AlarmManagementAction.getCanceledAlarmsFail(),
              SharedActions.showMessage({ error: 'Failed to load canceled alarms' }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  public getAlarmHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.getAlarmHistory),
      switchMap(({ filter }) => {
        return this.alarmsService.getAlarmHistory(filter)
          .pipe(
            switchMap(alarms => [
              AlarmManagementAction.getAlarmHistorySuccess({ alarms }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
            catchError(error => [
              AlarmManagementAction.getAlarmHistoryFail(),
              SharedActions.showMessage({ error: 'Failed to load alarm history' }),
              AlarmManagementAction.setAlarmsLoading({ isLoading: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  public createAlarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.createAlarm),
      switchMap(({ createAlarm }) => {
        return this.alarmsService.createAlarm(createAlarm)
          .pipe(
            switchMap(({ alarmId }) => [
              AlarmManagementAction.createAlarmSuccess({ alarmId }),
              AlarmManagementAction.startGetActiveAlarms(), // Refresh active alarms
              SharedActions.showMessage({ success: `Alarm created successfully. ID: ${alarmId}` }),
            ]),
            catchError(error => [
              AlarmManagementAction.createAlarmFail(),
              SharedActions.showMessage({ error: 'Failed to create alarm' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public cancelAlarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.cancelAlarm),
      switchMap(({ alarmId }) => {
        return this.alarmsService.cancelAlarmById(alarmId)
          .pipe(
            switchMap(() => [
              AlarmManagementAction.cancelAlarmSuccess(),
              AlarmManagementAction.startGetActiveAlarms(), // Refresh active alarms
              AlarmManagementAction.startGetCanceledAlarms(), // Refresh canceled alarms
              SharedActions.showMessage({ success: 'Alarm canceled successfully' }),
            ]),
            catchError(error => [
              AlarmManagementAction.cancelAlarmFail(),
              SharedActions.showMessage({ error: 'Failed to cancel alarm' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public checkActiveAlarm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmManagementAction.checkActiveAlarm),
      switchMap(({ locationId }) => {
        return this.alarmsService.checkActiveAlarm(locationId)
          .pipe(
            switchMap(checkResult => [
              AlarmManagementAction.checkActiveAlarmSuccess({ checkResult }),
            ]),
            catchError(error => [
              AlarmManagementAction.checkActiveAlarmFail(),
              SharedActions.showMessage({ error: 'Failed to check active alarm' }),
            ]),
          );
      }),
      share(),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private alarmsService: AlarmManagementService,
  ) {
  }
}
